import * as React from 'react';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import {
    TooltipComponent,
    LegendComponent,
    GridComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { Box, Typography } from '@mui/material';
import type {
    PieSeriesOption,
    TooltipComponentOption,
    LegendComponentOption,
    GridComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | PieSeriesOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
>;

echarts.use([PieChart, TooltipComponent, LegendComponent, GridComponent, SVGRenderer]);

export interface CustomerBaseProfileSegment {
    key: string;
    label: string;
    customer_count: number;
    percentage: number;
}

interface CustomerBaseProfileData {
    total_customers?: number;
    segments?: CustomerBaseProfileSegment[];
}

const SEGMENT_COLORS: Record<string, string> = {
    no_phone: '#ff7875',
    phone_only: '#faad14',
    company_details: '#52c41a',
};

interface Props {
    data?: CustomerBaseProfileData | null;
}

const CustomerBaseProfilePieChart: React.FC<Props> = ({ data }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    const segments = data?.segments || [];
    const totalCustomers = Number(data?.total_customers) || 0;

    React.useEffect(() => {
        if (!segments.length) {
            if (chartInstance.current) {
                chartInstance.current.setOption({ series: [{ data: [] }] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const option: ECOption = {
                tooltip: {
                    trigger: 'item',
                    formatter: (params: any) => {
                        const value = Number(params.value) || 0;
                        const percent = Number(params.percent || 0).toFixed(1);
                        return `${params.name}<br/>${value.toLocaleString()} customers (${percent}%)`;
                    },
                },
                legend: {
                    orient: 'vertical',
                    right: 0,
                    top: 'middle',
                },
                series: [
                    {
                        name: 'Customer profile health',
                        type: 'pie',
                        radius: ['42%', '72%'],
                        center: ['38%', '50%'],
                        avoidLabelOverlap: true,
                        itemStyle: {
                            borderRadius: 6,
                            borderColor: '#fff',
                            borderWidth: 2,
                        },
                        label: {
                            show: true,
                            formatter: (params: any) => {
                                const percent = Number(params.percent || 0).toFixed(1);
                                return `${percent}%`;
                            },
                        },
                        labelLine: {
                            show: true,
                            length: 12,
                            length2: 8,
                        },
                        data: segments.map((segment) => ({
                            name: segment.label,
                            value: segment.customer_count,
                            itemStyle: {
                                color: SEGMENT_COLORS[segment.key] || undefined,
                            },
                        })),
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [segments, totalCustomers]);

    if (!segments.length) {
        return null;
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                {totalCustomers.toLocaleString()} customers registered in the selected date range
            </Typography>
            <div ref={chartRef} style={{ width: '100%', height: 360 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 1 }}>
                {segments.map((segment) => (
                    <Typography key={segment.key} variant="body2" color="text.secondary">
                        {segment.label}: {segment.customer_count.toLocaleString()} ({segment.percentage}%)
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

export default CustomerBaseProfilePieChart;
